import { vestResolver } from '@hookform/resolvers/vest'
import { ManyLoansView } from '@sec-loans-ui/common/steps/assistance-step/components/ipf-loans-dropdown/components/ManyLoansView'
import { OtherDebtorsSection } from '@sec-loans-ui/common/steps/assistance-step/components/ipf-loans-dropdown/components/OtherDebtorsSection'
import { SingleLoanView } from '@sec-loans-ui/common/steps/assistance-step/components/ipf-loans-dropdown/components/SingleLoanView'
import { IFP_CHARGE_AMOUNT } from '@sec-loans-ui/common/steps/assistance-step/components/ipf-loans-dropdown/constants'
import { createFormValidator } from '@sec-loans-ui/common/steps/assistance-step/components/ipf-loans-dropdown/formValidator'
import { LoanDropdown } from '@sec-loans-ui/common/steps/assistance-step/components/LoanDropdown'
import { RootStepType } from '@sec-loans-ui/constants'
import { getIfpLoanByCreditId } from '@sec-loans-ui/utils/helpers'
import { assertValue } from '@shared/helpers'
import { IfpLoan } from '@shared/sec-loans'
import { ImplicitlySubmittableForm } from '@ui-common/components/ImplicitlySubmittableForm'
import { useWizardForm } from '@ui-common/hooks'
import { TightSectionContent } from '@ui-components/containers/TightSectionContent'
import { DataSummaryItem } from '@ui-components/form/DataSummary'
import { useEffect } from 'react'
import { FormProvider } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { OnCreatedIfpApplicationData } from '../../../../../ifp-loans/types'
import { useNewIfpApplicationMachineLogic } from './useNewIfpApplicationMachineLogic'

export interface IfpLoansDropdownWizardData {
  selectedCreditId?: string
}

export const IfpLoansDropdown = ({
  isOpenByDefault,
  onCreatedIfpLoansApplication,
  ifpLoans,
}: {
  isOpenByDefault: boolean
  onCreatedIfpLoansApplication: (data: OnCreatedIfpApplicationData) => void
  ifpLoans: IfpLoan[]
}) => {
  const { formatMessage } = useIntl()

  const { stateValue, createdApplicationId, submit, openErrorModal, analyticsService } =
    useNewIfpApplicationMachineLogic()

  const firstIfpLoan = ifpLoans[0]
  const hasOneSecLoan = ifpLoans.length === 1 && !!firstIfpLoan

  const formMethods = useWizardForm<IfpLoansDropdownWizardData>({
    defaultValues: {
      selectedCreditId: hasOneSecLoan ? firstIfpLoan.creditId : undefined,
    },
    resolver: vestResolver(createFormValidator(formatMessage)),
  })

  const watchSelectedCreditId = formMethods.watch('selectedCreditId')
  const selectedLoan = watchSelectedCreditId ? getIfpLoanByCreditId(ifpLoans, watchSelectedCreditId) : undefined

  useEffect(() => {
    switch (stateValue) {
      case 'submitFailed': {
        openErrorModal()
        break
      }
      case 'submitCompleted': {
        onCreatedIfpLoansApplication({
          applicationId: assertValue(createdApplicationId, 'createdApplicationId'),
          creditId: assertValue(selectedLoan?.creditId, 'selectedLoan?.creditId'),
        })
        break
      }
    }
  }, [stateValue, createdApplicationId, onCreatedIfpLoansApplication, openErrorModal, selectedLoan])

  const onSubmit = (validatedData: IfpLoansDropdownWizardData) => {
    const creditId = assertValue(validatedData.selectedCreditId, 'validatedData.selectedCreditId')
    analyticsService.sendStepCustomEvent(RootStepType.Assistance, 'CreateNewIfpApplication')
    submit({ creditId })
  }

  const otherDebtors = selectedLoan?.otherDebtors
  const hasCoDebtors = !!otherDebtors && otherDebtors.length > 0

  return (
    <FormProvider {...formMethods}>
      <ImplicitlySubmittableForm onSubmit={formMethods.handleSubmit(onSubmit)}>
        <LoanDropdown
          header={formatMessage({ id: 'loans-assistance-ifp-loans-dropdown-title' })}
          isOpenByDefault={isOpenByDefault}
          testId="ifp-loans-dropdown"
          isLoadingButtonView={stateValue === 'submitting'}
        >
          <TightSectionContent>
            {hasOneSecLoan ? <SingleLoanView ifpLoan={firstIfpLoan} /> : <ManyLoansView ifpLoans={ifpLoans} />}
            {hasCoDebtors && <OtherDebtorsSection otherDebtors={otherDebtors} />}
            <DataSummaryItem
              id={'ifp-loans-price'}
              label={formatMessage({ id: 'loans-assistance-ifp-loans-price-info-label' })}
              value={formatMessage(
                { id: 'loans-assistance-ifp-loans-price-info-description' },
                { chargeAmount: IFP_CHARGE_AMOUNT },
              )}
            />
          </TightSectionContent>
        </LoanDropdown>
      </ImplicitlySubmittableForm>
    </FormProvider>
  )
}
